import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';
import { usePlatformTracker } from '@/hooks/usePlatformTracker';
import { useFcmTokenRefresh } from '@/hooks/useFcmTokenRefresh';

// Logger para autenticação (só aparece em desenvolvimento)
const authLogger = createLogger('Auth');

// Tipo de role do aplicativo (inclui tenant_supervisor)
type AppRole = 'root_admin' | 'tenant_admin' | 'tenant_supervisor' | 'agent' | 'user';

interface UserRole {
  id: string;
  role: AppRole;
  tenant_id: string | null;
}

// Tipo de motivo de suspensão do tenant
type SuspensionReason = 'multiple_free_tenants' | 'trial_expired' | 'payment_failed' | null;

interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  max_agents: number;
  max_channels: number;
  trial_ends_at: string | null;
  created_at: string;
  suspension_reason: SuspensionReason;
  subscription_status: string | null; // Status da subscription Stripe: active, past_due, canceled, etc.
  crm_enabled: boolean; // Indica se o módulo CRM está ativo para este tenant
  financial_enabled: boolean; // Indica se o módulo Financeiro está ativo para este tenant
  tasks_enabled: boolean; // Indica se o módulo de Tarefas está ativo para este tenant
  affiliate_enabled: boolean; // Indica se o módulo Indicação/Afiliados está ativo para este tenant
  knowledge_enabled: boolean; // v2026-07-09: Indica se o módulo Base de Conhecimento (Wiki) está ativo para este tenant
  // v1.2.0: Classificação do tenant — 'customer' (cliente comercial) ou 'wl_internal'
  // (tenant interno usado pelo operador WL). Default 'customer' para compatibilidade.
  kind: 'customer' | 'wl_internal';
  // v2026-07-24: URL da logo do tenant — usada pelo TenantSwitcher para exibir
  // o logotipo da empresa no seletor de contas. Pode ser null se não configurada.
  logo_url: string | null;
}


// Interface para dados do perfil do usuário
interface UserProfile {
  full_name: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  roles: UserRole[];
  loading: boolean;
  dataLoaded: boolean; // Flag que indica se os dados do usuário foram completamente carregados
  isRootAdmin: boolean;
  isTenantMember: boolean;
  isTenantAdmin: boolean;
  isAgent: boolean; // v1.1.0: Indica se usuário é apenas agent (não admin/supervisor) no tenant atual
  // v1.2.0: True quando TODAS as memberships ativas são de tenants `wl_internal`.
  // Usado para rotear operador WL (que foi auto-sincronizado no tenant interno)
  // diretamente para /wl em vez de /tenant.
  isWlInternalOnlyMember: boolean;
  hasPendingInvite: boolean; // Indica se usuário tem convite pendente

  currentTenant: Tenant | null;
  availableTenants: Tenant[]; // Lista de todos os tenants vinculados ao usuário
  profile: UserProfile | null;
  isFirstAccess: boolean; // Indica se é primeiro acesso após criar tenant
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string, phone?: string) => Promise<{ data: { user: { identities?: { id: string }[] } | null } | null; error: Error | null }>;
  signOut: (onComplete?: () => void) => void;
  refreshProfile: () => Promise<void>;
  refreshTenants: () => Promise<Tenant[]>; // Atualiza lista de tenants disponíveis
  refreshCurrentTenant: () => Promise<Tenant | null>; // Atualiza apenas o tenant atual (após checkout Stripe)
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
  resendConfirmationEmail: (email: string) => Promise<{ error: Error | null }>; // Reenvia email de confirmação
  clearFirstAccess: () => void; // Limpa flag de primeiro acesso
  switchTenant: (tenantId: string) => void; // Alterna entre tenants
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]); // Todos os tenants do usuário
  const [isTenantOwner, setIsTenantOwner] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false); // Flag que indica dados do usuário carregados
  const [isFirstAccess, setIsFirstAccess] = useState(false);
  const [hasPendingInvite, setHasPendingInvite] = useState(false); // Convites pendentes para o usuário

  // Refs para evitar problemas de closure e re-fetches desnecessários
  const userIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const initialLoadDoneRef = useRef(false);
  // Flag para bloquear processamento durante logout ativo
  const isLoggingOutRef = useRef(false);

  // Rastrear plataforma de acesso (PWA vs Browser) — fire-and-forget, uma vez por sessão
  usePlatformTracker(user?.id ?? null);

  // Mantém o token FCM do dispositivo atualizado no backend após login e ao
  // voltar o foco do app — necessário para o iOS PWA, onde o token pode
  // rotacionar e o device_type/device_name precisam refletir o origin real.
  useFcmTokenRefresh(user?.id ?? null);


  const isRootAdmin = roles.some(r => r.role === 'root_admin' && r.tenant_id === null);
  const isTenantAdmin = isTenantOwner || isRootAdmin;
  const isTenantMember = currentTenant !== null;

  // v1.2.0: True quando o usuário só é membro de tenants do tipo `wl_internal`.
  // Operadores WL que foram auto-sincronizados como membros do tenant interno
  // (sem nenhum tenant comercial) caem nesse caso — devem ser roteados para /wl.
  const isWlInternalOnlyMember =
    availableTenants.length > 0 &&
    availableTenants.every(t => t.kind === 'wl_internal');

  // v1.1.0: Verifica se usuário é apenas agent (não tem role de admin/supervisor no tenant atual)
  // Usado para redirecionar agentes diretamente para /tenant/chat ao invés de /tenant
  const isAgent = currentTenant !== null && 
    roles.some(r => r.role === 'agent' && r.tenant_id === currentTenant.id) &&
    !roles.some(r => 
      (r.role === 'root_admin' || r.role === 'tenant_admin' || r.role === 'tenant_supervisor') &&
      (r.tenant_id === currentTenant.id || r.tenant_id === null)
    );


  const fetchUserData = async (userId: string) => {
    // Evitar fetches simultâneos
    if (isFetchingRef.current) {
      authLogger.debug('Fetch already in progress, skipping');
      return;
    }
    // CRÍTICO: Se logout coordenado já começou (nesta aba ou em outra via
    // BroadcastChannel), não disparar RPCs. Evita reidratar sessão em abas
    // que já deveriam estar deslogadas após N ciclos de login/logout.
    if (isLoggingOutRef.current) {
      authLogger.debug('Fetch ignorado: logout em andamento');
      return;
    }
    isFetchingRef.current = true;

    try {
      // Server-side: substitui SELECT direto em profiles por RPC get_my_profile
      const { data: profileRows } = await supabase.rpc('get_my_profile');
      const profileData = Array.isArray(profileRows) ? profileRows[0] : null;

      if (profileData) {
        setProfile({
          full_name: profileData.full_name,
          avatar_url: profileData.avatar_url
        });
      }

      // Server-side: substitui SELECT direto em user_roles por RPC get_my_user_roles
      const { data: rolesRows, error: rolesError } = await supabase.rpc('get_my_user_roles');

      if (rolesError) throw rolesError;
      setRoles(((rolesRows || []) as unknown) as UserRole[]);

      // v1.1.0: Migrado para RPC server-side `get_my_tenant_memberships`.
      // Substitui SELECT direto em tenant_members + JOIN com tenants, evitando
      // múltiplas requisições repetidas a tenant_members?user_id=eq.<uuid>.
      const { data: membershipsRpc, error: membershipError } = await (supabase.rpc as any)(
        'get_my_tenant_memberships',
      );

      // Normaliza resposta da RPC para o mesmo formato esperado pelo restante
      // do bloco (membership.tenants) — alteração CIRÚRGICA, sem reescrever a lógica.
      const membershipsData = (membershipsRpc || []).map((row: any) => ({
        tenant_id: row.tenant_id,
        is_owner: row.is_owner,
        tenants: {
          id: row.id,
          name: row.name,
          slug: row.slug,
          status: row.status,
          plan: row.plan,
          max_agents: row.max_agents,
          max_channels: row.max_channels,
          trial_ends_at: row.trial_ends_at,
          created_at: row.created_at,
          suspension_reason: row.suspension_reason,
          subscription_status: row.subscription_status,
          crm_enabled: row.crm_enabled,
          financial_enabled: row.financial_enabled,
          tasks_enabled: row.tasks_enabled,
          affiliate_enabled: row.affiliate_enabled,
          knowledge_enabled: row.knowledge_enabled,
          kind: row.kind, // v1.2.0: classificação do tenant
          logo_url: row.logo_url ?? null, // v2026-07-24: logo do tenant
        },
      }));


      if (membershipError) {
        authLogger.error('Error fetching membership', membershipError);
        setIsTenantOwner(false);
        setAvailableTenants([]);
      } else if (membershipsData && membershipsData.length > 0) {
        // Mapear todos os tenants disponíveis
        const allTenants: Tenant[] = membershipsData
          .filter((m: any) => m.tenants)
          .map((m: any) => {
            const t = m.tenants;
            return {
              id: t.id,
              name: t.name,
              slug: t.slug,
              status: t.status,
              plan: t.plan,
              max_agents: t.max_agents,
              max_channels: t.max_channels,
              trial_ends_at: t.trial_ends_at,
              created_at: t.created_at,
              suspension_reason: t.suspension_reason as SuspensionReason,
              subscription_status: t.subscription_status || null,
              crm_enabled: t.crm_enabled ?? false,
              financial_enabled: t.financial_enabled ?? false,
              tasks_enabled: t.tasks_enabled ?? false,
              affiliate_enabled: t.affiliate_enabled ?? false,
              knowledge_enabled: t.knowledge_enabled ?? true,
              kind: (t.kind === 'wl_internal' ? 'wl_internal' : 'customer'),
              logo_url: t.logo_url ?? null, // v2026-07-24: logo do tenant
            };
          });
        
        setAvailableTenants(allTenants);
        
        // Determinar tenant ativo: último usado (localStorage) ou primeiro da lista
        const savedTenantId = localStorage.getItem('currentTenantId');
        const savedMembership = savedTenantId 
          ? membershipsData.find(m => m.tenant_id === savedTenantId)
          : null;
        const activeMembership = savedMembership || membershipsData[0];
        
        const tenantData = Array.isArray(activeMembership.tenants) 
          ? activeMembership.tenants[0] 
          : activeMembership.tenants;
        
        if (tenantData) {
          // Detecta primeiro acesso: tenant criado há menos de 5 minutos
          const createdAt = new Date(tenantData.created_at);
          const now = new Date();
          const minutesSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60);
          const wasJustCreated = minutesSinceCreation < 5;
          
          // Verifica localStorage para não mostrar welcome repetidamente
          const welcomeShownKey = `welcome_shown_${tenantData.id}`;
          const welcomeAlreadyShown = localStorage.getItem(welcomeShownKey) === 'true';
          
          setIsFirstAccess(wasJustCreated && !welcomeAlreadyShown);
          
          setCurrentTenant({
            id: tenantData.id,
            name: tenantData.name,
            slug: tenantData.slug,
            status: tenantData.status,
            plan: tenantData.plan,
            max_agents: tenantData.max_agents,
            max_channels: tenantData.max_channels,
            trial_ends_at: tenantData.trial_ends_at,
            created_at: tenantData.created_at,
            suspension_reason: tenantData.suspension_reason as SuspensionReason,
            subscription_status: tenantData.subscription_status || null,
            crm_enabled: tenantData.crm_enabled ?? false,
            financial_enabled: tenantData.financial_enabled ?? false,
            tasks_enabled: tenantData.tasks_enabled ?? false,
            affiliate_enabled: (tenantData as { affiliate_enabled?: boolean }).affiliate_enabled ?? false,
            knowledge_enabled: (tenantData as { knowledge_enabled?: boolean }).knowledge_enabled ?? true,
            kind: ((tenantData as { kind?: string }).kind === 'wl_internal' ? 'wl_internal' : 'customer'),
            // v2026-07-24: logo do tenant para o seletor de empresas
            logo_url: (tenantData as { logo_url?: string | null }).logo_url ?? null,

          });
          
          // Salvar tenant ativo no localStorage
          localStorage.setItem('currentTenantId', tenantData.id);
          
          // SINCRONIZAR sessão ativa com o banco de dados
          // Isso garante que get_current_user_tenant_id() retorne o tenant correto
          try {
            await supabase.rpc('set_active_tenant', { p_tenant_id: tenantData.id });
            authLogger.debug('Sessão ativa sincronizada com banco', { tenantId: tenantData.id });
          } catch (syncError) {
            authLogger.warn('Falha ao sincronizar sessão ativa (não crítico)', syncError);
          }
          
          setIsTenantOwner(activeMembership.is_owner === true);
        }
      } else {
        setIsTenantOwner(false);
        setIsFirstAccess(false);
        setAvailableTenants([]);
      }

      // Server-side: verifica convites pendentes via RPC (resolve email pelo auth.uid)
      try {
        const { data: hasInvite, error: inviteError } = await supabase.rpc('has_pending_tenant_invite');
        if (inviteError) {
          authLogger.warn('Erro ao verificar convites pendentes', inviteError);
          setHasPendingInvite(false);
        } else {
          setHasPendingInvite(!!hasInvite);
          authLogger.debug('Pending invites check', { hasPendingInvite: !!hasInvite });
        }
      } catch (e) {
        authLogger.warn('Falha ao verificar convites pendentes (não crítico)', e);
        setHasPendingInvite(false);
      }
    } catch (error) {
      authLogger.error('Error fetching user data', error);
      setRoles([]);
      setCurrentTenant(null);
      setIsTenantOwner(false);
      setProfile(null);
    } finally {
      setLoading(false);
      setDataLoaded(true); // CORREÇÃO: Marcar dados como carregados após fetchUserData
      initialLoadDoneRef.current = true;
      isFetchingRef.current = false;
    }
  };

  // =========================================================================
  // LOGOUT COORDENADO CROSS-TAB
  //
  // Após o redesign WL, cada aba passou a executar mais RPCs autenticadas
  // (`is_wl_user`, `wl_get_my_permissions`, `get_my_tenant_memberships`,
  // `set_active_tenant`, etc.). Ao deslogar com várias abas abertas, cada
  // aba "descobria" o logout sozinha pelo storage event do supabase-js,
  // disparando cascatas de SIGNED_OUT, requests 401 e cleanups concorrentes.
  // Após 3+ ciclos isso acumulava timers/contexts presos e travava a UI.
  //
  // Solução: usar `BroadcastChannel('uaizy-auth')` para a aba que está
  // deslogando avisar TODAS as outras antes de remover o token. As outras
  // marcam `isLoggingOutRef = true`, ignoram eventos do supabase e fazem
  // hard reload para `/auth`, destruindo todo o estado em memória.
  // =========================================================================
  const AUTH_CHANNEL_NAME = 'uaizy-auth';
  const LOGOUT_SIGNAL_KEY = 'uaizy:logout-signal';

  /** Limpa estados locais de auth (usado tanto no logout local quanto cross-tab) */
  const resetAuthState = () => {
    userIdRef.current = null;
    initialLoadDoneRef.current = false;
    isFetchingRef.current = false;
    setUser(null);
    setSession(null);
    setRoles([]);
    setCurrentTenant(null);
    setAvailableTenants([]);
    setIsTenantOwner(false);
    setProfile(null);
  };

  /** Remove tokens do Supabase e chaves locais do app */
  const clearAuthStorage = () => {
    try {
      const supabasePattern = /^sb-.*-auth-token/;
      Object.keys(localStorage)
        .filter((key) => supabasePattern.test(key))
        .forEach((key) => localStorage.removeItem(key));
      Object.keys(sessionStorage)
        .filter((key) => supabasePattern.test(key))
        .forEach((key) => sessionStorage.removeItem(key));
      localStorage.removeItem('currentTenantId');
    } catch (e) {
      authLogger.warn('Erro ao limpar storage', e);
    }
  };

  useEffect(() => {
    // -----------------------------------------------------------------------
    // Listener cross-tab: outras abas notificam logout via BroadcastChannel
    // (fallback via `storage` event para navegadores antigos / contextos
    // onde BroadcastChannel não esteja disponível).
    // -----------------------------------------------------------------------
    let channel: BroadcastChannel | null = null;

    const handleRemoteLogout = () => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;
      // Limpa storage do Supabase imediatamente para evitar reidratação
      clearAuthStorage();
      // Se já estamos em /auth, evita reload que causaria loop de pisca-pisca.
      if (window.location.pathname === '/auth') {
        authLogger.info('Logout cross-tab recebido — já em /auth, apenas resetando estado');
        // Liberamos flags e zeramos estado para permitir novo login.
        resetAuthState();
        isLoggingOutRef.current = false;
        initialLoadDoneRef.current = true;
        setLoading(false);
        return;
      }
      authLogger.info('Logout cross-tab recebido — redirecionando para /auth');
      // Hard reload: destrói TanStack Query, Realtime, contexts e timers.
      // NÃO chamamos resetAuthState() aqui — o reload já destrói toda a árvore
      // React. Mexer em state primeiro causaria re-render cascata (WL guards,
      // Navigate, fechamento de Realtime) ANTES do replace, travando a UI.
      window.location.replace('/auth');
    };


    try {
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
        channel.onmessage = (ev) => {
          if (ev?.data?.type === 'LOGOUT_STARTED') {
            handleRemoteLogout();
          }
        };
      }
    } catch (e) {
      authLogger.warn('BroadcastChannel indisponível', e);
    }

    const onStorage = (ev: StorageEvent) => {
      if (ev.key === LOGOUT_SIGNAL_KEY && ev.newValue) {
        handleRemoteLogout();
      }
    };
    window.addEventListener('storage', onStorage);

    // -----------------------------------------------------------------------
    // Set up auth state listener FIRST
    // -----------------------------------------------------------------------
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        // CRÍTICO: Ignorar TODOS os eventos durante logout ativo (local ou cross-tab)
        if (isLoggingOutRef.current) {
          authLogger.debug('Ignorando evento durante logout ativo', { event });
          return;
        }

        const newUserId = currentSession?.user?.id ?? null;
        const currentUserId = userIdRef.current;

        authLogger.info('Event: ' + event, { newUserId, currentUserId, initialDone: initialLoadDoneRef.current });

        // CRÍTICO: Só processar se o userId realmente mudou
        // Isso ignora TOKEN_REFRESHED, INITIAL_SESSION duplicados, etc.
        if (newUserId === currentUserId && initialLoadDoneRef.current) {
          authLogger.info('Same user, skipping re-fetch. Only updating session.');
          // Apenas atualizar session/token sem refetch de dados
          setSession(currentSession);
          return;
        }

        // Atualizar ref do userId
        userIdRef.current = newUserId;

        // Atualizar estados básicos
        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          // CRÍTICO: Detectar se é um NOVO login (usuário não estava logado antes)
          const isNewLogin = currentUserId === null && newUserId !== null;

          if (!initialLoadDoneRef.current || isNewLogin) {
            setLoading(true);
          }

          // Defer fetch para evitar deadlock com Supabase
          setTimeout(() => {
            fetchUserData(currentSession.user.id);
          }, 0);
        } else {
          // Sessão nula. Só tratar como logout real (com hard reload) se
          // realmente havia um usuário logado antes E o evento é SIGNED_OUT
          // explícito. Caso contrário (INITIAL_SESSION sem sessão na /auth),
          // apenas finaliza o carregamento — evita loop de reload na tela de login.
          const wasLoggedIn = currentUserId !== null;
          const isExplicitSignOut = event === 'SIGNED_OUT';

          if (wasLoggedIn && isExplicitSignOut) {
            authLogger.info('SIGNED_OUT real detectado via onAuthStateChange — redirecionando');
            handleRemoteLogout();
          } else {
            authLogger.debug('Sessão nula sem logout prévio — fluxo normal de /auth', { event });
            initialLoadDoneRef.current = true;
            setLoading(false);
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
      if (isLoggingOutRef.current) return;
      const userId = existingSession?.user?.id ?? null;

      authLogger.info('Initial getSession', { userId });

      userIdRef.current = userId;
      setSession(existingSession);
      setUser(existingSession?.user ?? null);

      if (existingSession?.user) {
        fetchUserData(existingSession.user.id);
      } else {
        setLoading(false);
        setDataLoaded(true);
        initialLoadDoneRef.current = true;
      }
    });

    return () => {
      subscription.unsubscribe();
      window.removeEventListener('storage', onStorage);
      try { channel?.close(); } catch { /* noop */ }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  /**
   * Cadastra novo usuário
   * Retorna data junto com error para permitir detecção de email já cadastrado
   * (Supabase retorna identities=[] quando email já existe, não retorna erro)
   */
  /**
   * Cadastra novo usuário com nome e telefone opcional
   * Telefone é salvo no metadata e extraído pela trigger handle_new_user
   */
  const signUp = async (email: string, password: string, fullName: string, phone?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          phone: phone || null, // Inclui telefone no metadata para trigger
        },
      },
    });
    return { data, error: error as Error | null };
  };

  /**
   * Envia email de recuperação de senha
   * O usuário receberá um link para redefinir a senha
   */
  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?mode=reset`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  /**
   * Atualiza a senha do usuário (após clicar no link de recuperação)
   */
  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error: error as Error | null };
  };

  /**
   * Reenvia email de confirmação de cadastro
   * Usa o método resend do Supabase Auth
   */
  const resendConfirmationEmail = async (email: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  /**
   * Executa logout coordenado cross-tab.
   *
   * Fluxo:
   *  1. Marca flag de logout (silencia onAuthStateChange e fetchUserData).
   *  2. Publica `LOGOUT_STARTED` em BroadcastChannel + storage signal —
   *     todas as outras abas recebem e fazem hard reload para /auth.
   *  3. Limpa estados locais + storage do Supabase.
   *  4. Chama `supabase.auth.signOut({ scope: 'local' })` em background com
   *     timeout curto (não bloqueia UI se o navigator.locks estiver em fila).
   *  5. Faz `window.location.replace('/auth')` — destrói TanStack Query,
   *     Realtime, contexts e timers, evitando o acúmulo de estado que
   *     causava tela branca após 3+ ciclos de login/logout com várias abas.
   *
   * @param onComplete - mantido por compatibilidade; ignorado pois o
   *                     redirecionamento agora é sempre hard reload.
   */
  const signOut = (_onComplete?: () => void) => {
    authLogger.info('Iniciando logout coordenado...');

    // 1. Bloquear handlers desta aba ANTES de qualquer side-effect
    isLoggingOutRef.current = true;

    // 2. Avisar todas as outras abas ANTES de remover token
    try {
      if (typeof BroadcastChannel !== 'undefined') {
        const ch = new BroadcastChannel(AUTH_CHANNEL_NAME);
        ch.postMessage({ type: 'LOGOUT_STARTED', at: Date.now() });
        setTimeout(() => { try { ch.close(); } catch { /* noop */ } }, 50);
      }
    } catch (e) {
      authLogger.warn('Falha ao publicar LOGOUT_STARTED', e);
    }
    // Fallback storage event
    try {
      localStorage.setItem(LOGOUT_SIGNAL_KEY, String(Date.now()));
      setTimeout(() => {
        try { localStorage.removeItem(LOGOUT_SIGNAL_KEY); } catch { /* noop */ }
      }, 500);
    } catch (e) {
      authLogger.warn('Falha ao gravar signal de logout', e);
    }

    // 3. Capturar token ANTES de limpar storage para revogar a sessão remotamente.
    //    Acessamos diretamente o item do localStorage (já em memória, sem awaits).
    let accessToken: string | null = null;
    try {
      const supabasePattern = /^sb-.*-auth-token$/;
      const tokenKey = Object.keys(localStorage).find((k) => supabasePattern.test(k));
      if (tokenKey) {
        const raw = localStorage.getItem(tokenKey);
        if (raw) {
          const parsed = JSON.parse(raw);
          accessToken = parsed?.access_token ?? null;
        }
      }
    } catch (e) {
      authLogger.warn('Falha ao capturar access_token para revogação', e);
    }

    // 4. Limpar storage do Supabase localmente (síncrono e rápido).
    //    Não chamamos resetAuthState(): a árvore React vai morrer no replace abaixo
    //    e zerar state agora dispara cascata (WL guards, Navigate, Realtime close)
    //    que era o que causava o travamento da página antes do redirect.
    clearAuthStorage();

    // 5. Revogação remota fire-and-forget — não bloqueia a navegação.
    //    Usamos fetch keepalive para garantir que a requisição prossiga
    //    mesmo após o replace para /auth. Substitui supabase.auth.signOut(),
    //    que passa por _acquireLock (navigator.locks) entre abas e podia
    //    atrasar segundos quando havia múltiplas abas/RPCs autenticadas.
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;
      if (accessToken && supabaseUrl && anonKey) {
        fetch(`${supabaseUrl}/auth/v1/logout?scope=local`, {
          method: 'POST',
          headers: {
            apikey: anonKey,
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          keepalive: true,
        }).catch((e) => authLogger.warn('Falha na revogação remota (ignorada)', e));
      }
    } catch (e) {
      authLogger.warn('Exceção ao disparar revogação remota (ignorada)', e);
    }

    // 6. Hard reload — única forma confiável de destruir cache, realtime, timers
    //    e impedir que guards WL re-renderizem antes do redirect.
    authLogger.info('Redirecionando para /auth via hard reload');
    window.location.replace('/auth');
  };


  /**
   * Atualiza dados do perfil do usuário (após edição no modal)
   */
  const refreshProfile = async () => {
    if (!user?.id) return;
    
    try {
      // Server-side: substitui SELECT direto em profiles por RPC get_my_profile
      const { data: profileRows } = await supabase.rpc('get_my_profile');
      const profileData = Array.isArray(profileRows) ? profileRows[0] : null;

      if (profileData) {
        setProfile({
          full_name: profileData.full_name,
          avatar_url: profileData.avatar_url
        });
      }
    } catch (error) {
      authLogger.error('Error refreshing profile', error);
    }
  };

  /**
   * Atualiza lista de tenants disponíveis
   * Deve ser chamado após criar nova empresa para atualizar a listagem
   * Retorna a lista atualizada para uso imediato
   */
  const refreshTenants = async (): Promise<Tenant[]> => {
    if (!user?.id) return [];
    
    try {
      // v1.1.0: Migrado para RPC `get_my_tenant_memberships` (idem fetchUserData).
      const { data: membershipsRpc, error } = await (supabase.rpc as any)(
        'get_my_tenant_memberships',
      );

      const membershipsData = (membershipsRpc || []).map((row: any) => ({
        tenant_id: row.tenant_id,
        is_owner: row.is_owner,
        tenants: {
          id: row.id,
          name: row.name,
          slug: row.slug,
          status: row.status,
          plan: row.plan,
          max_agents: row.max_agents,
          max_channels: row.max_channels,
          trial_ends_at: row.trial_ends_at,
          created_at: row.created_at,
          suspension_reason: row.suspension_reason,
          subscription_status: row.subscription_status,
          crm_enabled: row.crm_enabled,
          financial_enabled: row.financial_enabled,
          tasks_enabled: row.tasks_enabled,
          affiliate_enabled: row.affiliate_enabled,
          knowledge_enabled: row.knowledge_enabled,
          kind: row.kind, // v1.2.0

        },
      }));

      if (error || !membershipsData) {
        authLogger.error('Error refreshing tenants', error);
        return availableTenants;
      }
      
      const allTenants: Tenant[] = membershipsData
        .filter((m: any) => m.tenants)
        .map((m: any) => {
          const t = m.tenants;
          return {
            id: t.id,
            name: t.name,
            slug: t.slug,
            status: t.status,
            plan: t.plan,
            max_agents: t.max_agents,
            max_channels: t.max_channels,
            trial_ends_at: t.trial_ends_at,
            created_at: t.created_at,
            suspension_reason: t.suspension_reason as SuspensionReason,
            subscription_status: t.subscription_status || null,
            crm_enabled: t.crm_enabled ?? false,
            financial_enabled: t.financial_enabled ?? false,
            tasks_enabled: t.tasks_enabled ?? false,
            affiliate_enabled: t.affiliate_enabled ?? false,
            knowledge_enabled: t.knowledge_enabled ?? true,
            kind: (t.kind === 'wl_internal' ? 'wl_internal' : 'customer'),

          };
        });
      
      setAvailableTenants(allTenants);
      authLogger.debug('Tenants refreshed', { count: allTenants.length });
      return allTenants;
    } catch (error) {
      authLogger.error('Error refreshing tenants', error);
      return availableTenants;
    }
  };

  /**
   * Limpa flag de primeiro acesso e marca no localStorage
   * Deve ser chamado após exibir mensagem de boas-vindas
   */
  const clearFirstAccess = () => {
    if (currentTenant?.id) {
      const welcomeShownKey = `welcome_shown_${currentTenant.id}`;
      localStorage.setItem(welcomeShownKey, 'true');
    }
    setIsFirstAccess(false);
  };

  /**
   * Alterna entre tenants disponíveis
   * Sincroniza com banco via RPC set_active_tenant e atualiza contexto local
   * VERSÃO 2.0: Agora sincroniza com tabela user_active_tenant no banco
   */
  const switchTenant = async (tenantId: string) => {
    const tenant = availableTenants.find(t => t.id === tenantId);
    if (tenant) {
      try {
        // SINCRONIZAR COM BANCO PRIMEIRO
        // Isso garante que get_current_user_tenant_id() e get_validated_tenant_id()
        // retornem o tenant correto nas próximas chamadas
        const { error } = await supabase.rpc('set_active_tenant', { 
          p_tenant_id: tenantId 
        });
        
        if (error) {
          authLogger.error('Erro ao sincronizar tenant ativo no banco', error);
          // Não abortar - ainda atualizar frontend para UX
        } else {
          authLogger.debug('Tenant ativo sincronizado com banco', { tenantId });
        }
      } catch (syncError) {
        authLogger.warn('Exceção ao sincronizar tenant (continuando)', syncError);
      }
      
      // Atualizar estado local (frontend)
      setCurrentTenant(tenant);
      localStorage.setItem('currentTenantId', tenantId);
      
      // CAMADA 1: Redirecionamento inteligente ao trocar tenant
      // Se o usuário está em rota CRM e o novo tenant não tem CRM habilitado,
      // redirecionar para o dashboard principal para evitar acesso indevido
      const currentPath = window.location.pathname;
      const isCrmRoute = currentPath.startsWith('/tenant/crm');
      
      if (isCrmRoute && !tenant.crm_enabled) {
        authLogger.warn('Redirecionando: tenant destino não possui módulo CRM', {
          tenantId,
          fromRoute: currentPath,
        });
        // Usar window.location.href para forçar reload completo do contexto
        window.location.href = '/tenant';
        return;
      }
    }
  };

  /**
   * Atualiza apenas os dados do tenant atual (após checkout Stripe)
   * Busca dados frescos do banco e atualiza contexto sem refetch completo
   */
  const refreshCurrentTenant = async (): Promise<Tenant | null> => {
    if (!currentTenant?.id) return null;
    
    try {
      authLogger.debug('Refreshing current tenant', { tenantId: currentTenant.id });
      
      const { data, error } = await supabase
        .from('tenants')
        .select(`
          id, name, slug, status, plan, 
          max_agents, max_channels, trial_ends_at, 
          created_at, suspension_reason, subscription_status, crm_enabled, financial_enabled, tasks_enabled, affiliate_enabled, knowledge_enabled, kind, logo_url
        `)
        .eq('id', currentTenant.id)
        .single();
      
      if (error || !data) {
        authLogger.error('Error refreshing current tenant', error);
        return null;
      }
      
      const updatedTenant: Tenant = {
        id: data.id,
        name: data.name,
        slug: data.slug,
        status: data.status,
        plan: data.plan,
        max_agents: data.max_agents,
        max_channels: data.max_channels,
        trial_ends_at: data.trial_ends_at,
        created_at: data.created_at,
        suspension_reason: data.suspension_reason as SuspensionReason,
        subscription_status: data.subscription_status || null,
        crm_enabled: data.crm_enabled ?? false,
        financial_enabled: data.financial_enabled ?? false,
        tasks_enabled: data.tasks_enabled ?? false,
        affiliate_enabled: (data as { affiliate_enabled?: boolean }).affiliate_enabled ?? false,
        knowledge_enabled: (data as { knowledge_enabled?: boolean }).knowledge_enabled ?? true,
        // v1.2.0: kind do tenant
        kind: ((data as { kind?: string }).kind === 'wl_internal' ? 'wl_internal' : 'customer'),
        // v2026-07-24: logo do tenant para o seletor de empresas
        logo_url: (data as { logo_url?: string | null }).logo_url ?? null,
      };

      
      // Atualizar currentTenant
      setCurrentTenant(updatedTenant);
      
      // Atualizar também na lista de availableTenants para consistência
      setAvailableTenants(prev => 
        prev.map(t => t.id === updatedTenant.id ? updatedTenant : t)
      );
      
      authLogger.debug('Current tenant refreshed', { plan: updatedTenant.plan });
      return updatedTenant;
    } catch (error) {
      authLogger.error('Exception refreshing current tenant', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      roles,
      loading,
      dataLoaded, // CORREÇÃO: Exportar flag de dados carregados
      isRootAdmin,
      isTenantMember,
      isTenantAdmin,
      isAgent, // v1.1.0: Exportar flag de agente
      isWlInternalOnlyMember, // v1.2.0: roteamento WL × tenant interno

      hasPendingInvite,
      currentTenant,
      availableTenants,
      profile,
      signIn,
      signUp,
      signOut,
      refreshProfile,
      refreshTenants,
      refreshCurrentTenant,
      resetPassword,
      updatePassword,
      resendConfirmationEmail,
      isFirstAccess,
      clearFirstAccess,
      switchTenant,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
